import React, { useState, useEffect } from 'react';
import { Card, message, Form, Select, Button, Row, Col, Image, Table, Input } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { SearchOutlined } from '@ant-design/icons';

const { Option } = Select;

// 课程体系列表组件 - 简化版，只显示图片、名称和定价
const CurriculumList = ({ 
  onCurriculumSelect, 
  enableSelection = false,
  selectedCurriculumId = null,
  searchPlaceholder = "搜索课程名称",
  showSearch = true,
  showHeader = true,
  cardTitle = "课程体系列表",
  pageSize: controlledPageSize,
  ...props
}) => {
  const [curriculums, setCurriculums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: controlledPageSize || 10,
    total: 0,
  });
  const [ageRangeOptions, setAgeRangeOptions] = useState([]);
  const { apiClient } = useAuth();

  // 定义表格列（只显示图片、名称和定价）
  const columns = [
    ...(enableSelection ? [{
      title: '选择',
      key: 'selection',
      width: 60,
      render: (_, record) => (
        <input 
          type="radio" 
          onChange={() => onCurriculumSelect && onCurriculumSelect(record)}
          checked={selectedCurriculumId === record.id}
        />
      ),
    }] : []),
    {
      title: '预览照片',
      dataIndex: 'pic',
      key: 'pic',
      width: 120,
      render: (pic) =>
        pic ? <Image src={pic} alt="pic" width={100} height={100} /> : <span>-</span>,
    },
    {
      title: '课程名称',
      dataIndex: 'curriculumName',
      key: 'curriculumName',
      width: 200,
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => price ? `¥${price}` : '-',
    },
  ];

  // 获取课程体系列表
  const fetchCurriculums = async (params = {}) => {
    setLoading(true);
    try {
      // 构建请求参数，仅包含非空值
      const requestParams = {
        pageNo: params.current || 1,
        pageSize: params.pageSize || pagination.pageSize,
      };
      
      // 只有当值存在时才添加到请求参数中
      if (params.curriculumName) {
        requestParams.curriculumName = params.curriculumName;
      }
      if (params.publicKbn !== undefined && params.publicKbn !== null && params.publicKbn !== '') {
        requestParams.publicKbn = params.publicKbn;
      }
      
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/curriculumSystem/pageList', requestParams);

      if (response.data && response.data.code === 200) {
        const list = response.data.data.list || [];
        setCurriculums(list);
        setPagination((prev) => ({
          ...prev,
          current: params.current || 1,
          pageSize: params.pageSize || pagination.pageSize,
          total: response.data.data.totalRecord,
        }));
      } else {
        message.error('获取课程体系列表失败');
        setCurriculums([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error) {
      console.error('获取课程体系列表失败:', error);
      message.error('获取课程体系列表失败');
      setCurriculums([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  // 获取年龄段选项
  const fetchAgeRangeOptions = async () => {
    try {
      const response = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'COURSE_AGE',
        colName: 'COURSE_AGE'
      });
      
      if (response.data && response.data.code === 200) {
        let rawData = response.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        
        const options = (rawData || []).map((item) => ({
          label: item.enumName,
          value: item.enumCode || item.enumName,
        }));
        
        setAgeRangeOptions(options);
      } else {
        console.error('获取年龄段选项失败:', response.data?.msg || '未知错误');
      }
    } catch (error) {
      console.error('获取年龄段选项异常:', error);
    }
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageSize: controlledPageSize || prev.pageSize }));
  }, [controlledPageSize]);

  useEffect(() => {
    fetchCurriculums({ pageSize: pagination.pageSize });
    fetchAgeRangeOptions(); // 加载年龄段选项
  }, [pagination.pageSize]);

  // 处理表格变化
  const handleTableChange = (newPagination) => {
    const tableValues = searchForm.getFieldsValue();
    fetchCurriculums({
      ...tableValues,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    const searchValues = searchForm.getFieldsValue();
    fetchCurriculums({
      ...searchValues,
      current: 1,
    });
  };

  // 重置
  const handleReset = () => {
    searchForm.resetFields();
    fetchCurriculums({ current: 1, pageSize: pagination.pageSize });
  };

  return (
    <Card title={showHeader ? cardTitle : null} {...props}>
      {showSearch && (
        <Form form={searchForm} layout="vertical" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="curriculumName" label="课程名称">
                <Input placeholder={searchPlaceholder} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="publicKbn" label="是否公开">
                <Select allowClear placeholder="请选择">
                  <Option value="1">公开</Option>
                  <Option value="0">不公开</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ageRange" label="适配年龄段">
                <Select allowClear placeholder="请选择适配年龄段">
                  {ageRangeOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                重置
              </Button>
            </Col>
          </Row>
        </Form>
      )}
      <Table
        columns={columns}
        dataSource={curriculums}
        rowKey="id"
        loading={loading}
        pagination={{ ...pagination, pageSize: pagination.pageSize }}
        onChange={(pagination) => handleTableChange(pagination)}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default CurriculumList;